import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ExtractionParser } from 'src/helpers/extraction/parseExtraction';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { createExtraction } from 'src/slices/extraction';
import { createMultiplePrejudices } from 'src/slices/prejudice';
import { fetchAllBaremes } from 'src/slices/bareme';
import { useDisplayedOrganization } from 'src/hooks/store/organization.hooks';
import { baremeSelectors, monetaryErosionSelectors } from 'src/store/selectors';
import { getAllMonetaryErosions } from 'src/slices/monetaryErosion';

const UploadReport: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { organization } = useDisplayedOrganization();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const { baremes, monetaryErosions } = useAppSelector((state) => ({
    baremes: baremeSelectors.selectAll(state),
    monetaryErosions: monetaryErosionSelectors.selectAll(state),
  }));

  useEffect(() => {
    dispatch(fetchAllBaremes());
    dispatch(getAllMonetaryErosions());
  }, [dispatch]);

  // Simulate progress over 45 seconds with faster progression
  useEffect(() => {
    let timer: NodeJS.Timeout;
    let startTime: number;
    
    if (isLoading) {
      setProgress(0);
      startTime = Date.now();
      const totalTime = 120 * 1000; // 45 seconds in milliseconds
      const intervalTime = 100; // Update every 100ms for smooth animation

      // Faster non-linear progress function
      const getProgressFromTime = (elapsedTime: number): number => {
        const normalizedTime = elapsedTime / totalTime; // 0 to 1
        
        // Faster progression with less slow middle phase
        let progress: number;
        
        if (normalizedTime < 0.15) {
          // First 15% of time -> 0-40% progress (very fast start)
          progress = (normalizedTime / 0.15) * 40;
        } else if (normalizedTime < 0.7) {
          // Next 55% of time -> 40-80% progress (moderate middle)
          const middleProgress = (normalizedTime - 0.15) / 0.55;
          progress = 40 + (middleProgress * 40);
        } else {
          // Last 30% of time -> 80-95% progress (fast end)
          const endProgress = (normalizedTime - 0.7) / 0.3;
          progress = 80 + (endProgress * 15);
        }
        
        return Math.min(progress, 95); // Cap at 95%
      };

      timer = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        const newProgress = getProgressFromTime(elapsedTime);
        
        setProgress(newProgress);
        
        if (newProgress >= 95 || elapsedTime >= totalTime) {
          clearInterval(timer);
        }
      }, intervalTime);
    } else {
      setProgress(0);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isLoading]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      if (file) {
        setSelectedFile(file);
      }
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile || !organization) return;

    setIsLoading(true);
    try {
      const { extraction: extractionOutput, procedure, victime } = await dispatch(
        createExtraction({
          dto: {
            file: selectedFile,
          },
          organizationId: organization._id,
        }),
      ).unwrap();
      const parser = new ExtractionParser();
      if (!extractionOutput.extractionData) {
        throw new Error('No extracted data');
      }
      const parsedPrejudices = await parser.parseExtractionPrejudices(
        victime,
        procedure,
        extractionOutput.extractionData,
        baremes,
        monetaryErosions,
      );
      await dispatch(
        createMultiplePrejudices({
          procedureId: procedure._id,
          dto: parsedPrejudices,
        }),
      ).unwrap();
      setProgress(100); // Complete the progress
      
      // Redirect to the victime page
      if (organization._id && victime._id) {
        navigate(`/organization/${organization._id}/client/${victime._id}`);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
      setSelectedFile(null);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  return (
      <Container 
        maxWidth="md" 
        sx={{ 
          mt: 10, // Account for navbar height
          mb: 4 
        }}
      >
        <Card>
          <CardHeader 
            title={t('dashboard.uploadReport.title')}
            sx={{ pb: 1 }}
          />
          <CardContent>
            {isLoading && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 2 }}>
                  {t('dashboard.uploadReport.processing')}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    mb: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  {Math.round(progress)}%
                </Typography>
              </Box>
            )}
            
            <Stack spacing={3}>
              <Typography variant="body1">
                {t('dashboard.uploadReport.instruction')}
              </Typography>
              
              <TextField
                type="file"
                fullWidth
                inputProps={{
                  accept:
                    'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx',
                }}
                onChange={handleFileChange}
                disabled={isLoading}
              />
              
              <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                <Button 
                  onClick={handleCancel} 
                  disabled={isLoading}
                  variant="outlined"
                  size="large"
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={!selectedFile || isLoading || !organization}
                  variant="contained"
                  size="large"
                  sx={{ flex: 1 }}
                >
                  {t('common.submit')}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
  );
};

export default UploadReport;